import { useEffect, useRef } from 'react';

import {
  ConditionalRender,
  DeviceTypeDetectionVariant,
  useDeviceTypeDetection,
} from '@crossnokaye/ux-common';

import { AtlasRouteIds } from '@/components/navigation/constants';
import { useAbsorbRedirect } from '@/components/navigation/hooks/use-absorb-redirect';
import { useLeftNavAccess } from '@/components/navigation/hooks/use-left-nav-access';
import { useZendeskRedirect } from '@/components/navigation/hooks/use-zendesk-redirect';
import {
  ZENDESK_HELP_CENTER_URL,
  ZENDESK_SITE_DOCUMENTATION_URL,
} from '@/components/navigation/left/constants';
import { getIconNameFromOrgName } from '@/components/navigation/utils';
import { SUPPORT_CONTACT_NUMBER } from '@/constants';
import { useUserFacilityRoles } from '@/hooks/query-hooks/use-user-facility-roles';
import { useAuth } from '@/hooks/use-auth';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { useCurrentRoute } from '@/hooks/use-current-route';
import { useLocalOnlineMode } from '@/hooks/use-local-online-mode';
import { useUser } from '@/hooks/use-user';
import { useNavStore } from '@/stores/nav';
import { goOffline } from '@/utils/go-offline';

import { CollapsedNav } from './collapsed/collapsed-nav';
import { ExpandedNav } from './expanded/expanded-nav';
export const MainNav = ({
  isExpanded,
  toggleExpanded,
  switchToFacilityMenu,
}: {
  isExpanded: boolean;
  toggleExpanded: () => void;
  switchToFacilityMenu: () => void;
}) => {
  const { currentFacility, facilityUrlName, orgName, orgUrlName } = useCurrentFacility();
  const currentRoute = useCurrentRoute();
  const access = useLeftNavAccess();
  const isLocalOnline = useLocalOnlineMode();

  const { logout, requestViewOnly, requestActive, isViewOnly } = useAuth();

  const {
    setAccessibleNavItems,
    setFacilityName,
    setOrgIcon,
    setOrgName,
    setRoleInfo,
    setSelectedNavItemId,
    setUserAccessIsLoading,
    setUserName,
    setExpanded,
  } = useNavStore();

  const { data: roleInfo } = useUserFacilityRoles();
  const { name } = useUser();

  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });
  const handleZendeskRedirect = useZendeskRedirect();
  const handleAtlasLearning = useAbsorbRedirect();

  useEffect(() => {
    setAccessibleNavItems(access);
  }, [access, setAccessibleNavItems]);

  useEffect(() => {
    if (name) {
      setUserName(name);
    }
  }, [name, setUserName]);

  useEffect(() => {
    if (orgName) {
      setOrgName(orgName);
    }
  }, [orgName, setOrgName]);

  useEffect(() => {
    if (currentFacility) {
      setFacilityName(currentFacility.displayName);
      const iconName = orgName ? getIconNameFromOrgName(orgName) : undefined;
      setOrgIcon(iconName);
    }
  }, [currentFacility, setFacilityName, setOrgIcon, orgName]);

  useEffect(() => {
    if (roleInfo?.roles) {
      setRoleInfo(roleInfo.roles.map((r) => r.displayName));
    }
  }, [roleInfo, setRoleInfo]);

  const isTogglingAccessRef = useRef(false);

  const handleSignOut = () => {
    setUserAccessIsLoading(true);
    logout();
  };

  const handleToggleActive = async () => {
    // Re-entrancy guard: the async token request + cross-app broadcast must settle before
    // another toggle, or a double-click fires duplicate token fetches and racing publishes.
    if (isTogglingAccessRef.current) return;
    isTogglingAccessRef.current = true;
    setUserAccessIsLoading(true);
    // requestActive → clear the shared signal and force a fresh interactive re-login to
    // regain write (a full-page redirect; the spinner covers the brief pre-redirect gap).
    // requestViewOnly → go view-only silently and propagate to the sister app.
    // Both own their errors internally; keep the spinner up until the work settles either way.
    try {
      await (isViewOnly ? requestActive() : requestViewOnly());
    } finally {
      isTogglingAccessRef.current = false;
      setUserAccessIsLoading(false);
    }
  };

  const handleNavItemClick = (id: string) => {
    // expanded state is shared between mobile and desktop, so we need to close it on mobile,
    // to need for extra click to close nav on navigation (unless switching to facility config nav)
    if (isMobile && id !== AtlasRouteIds.FacilityConfig) {
      setExpanded(false);
    }

    switch (id) {
      case AtlasRouteIds.Support:
        handleSupport();
        break;
      case AtlasRouteIds.HelpCenter:
        handleHelpCenter();
        break;
      case AtlasRouteIds.SiteNavigation:
        handleSiteDocuments();
        break;
      case AtlasRouteIds.AtlasLearning:
        handleAtlasLearning();
        break;
      case AtlasRouteIds.OfflineEscapeHatch:
        goOffline(isLocalOnline, orgUrlName, facilityUrlName);
        break;
      default:
        setSelectedNavItemId(id);
        break;
    }
  };

  const handleHelpCenter = () => {
    handleZendeskRedirect(ZENDESK_HELP_CENTER_URL);
  };

  const handleSiteDocuments = () => {
    handleZendeskRedirect(ZENDESK_SITE_DOCUMENTATION_URL);
  };

  const handleSupport = () => {
    const telLink = `tel:${SUPPORT_CONTACT_NUMBER.replace(/[-\s()]/g, '')}`;

    if (isMobile) {
      window.open(telLink, '_blank');
    }
  };

  useEffect(() => {
    if (currentRoute) {
      setSelectedNavItemId(currentRoute);
    }
  }, [currentRoute, setSelectedNavItemId]);

  return (
    <ConditionalRender
      condition={isExpanded}
      fallback={
        <CollapsedNav
          isViewOnly={isViewOnly}
          toggleExpanded={toggleExpanded}
          onSelect={handleNavItemClick}
          onSignOut={handleSignOut}
          onToggleActive={handleToggleActive}
        />
      }
    >
      <ExpandedNav
        isViewOnly={isViewOnly}
        toggleExpanded={toggleExpanded}
        switchToFacilityMenu={switchToFacilityMenu}
        onSelect={handleNavItemClick}
        onSignOut={handleSignOut}
        onToggleActive={handleToggleActive}
      />
    </ConditionalRender>
  );
};
